import { z } from 'zod';
import { PlainDate } from './common.js';

/**
 * Data and year rollover, and the support tools (PRD §18, §19 "CSV import", APP-053): CSV imports
 * previewed before anything is saved and applied as one audited batch, next year's budgets as a
 * draft, archiving a year, reopening a closed award and releasing a stuck Shareworks claim.
 */

const Ref = z.object({ id: z.string(), name: z.string() });
const Count = z.number().int().nonnegative();
const DateTime = z.string();
const Reason = z.string().trim().min(1, 'Say why.').max(2000);
const Year = z.coerce.number().int().min(2000).max(2100);
const Amounts = z.object({ cashCents: z.number().int(), options: z.number().int() });

// ---------------------------------------------------------------------------------------------
// CSV imports
// ---------------------------------------------------------------------------------------------

export const ImportKind = z.enum([
  'directory_fields',
  /** Add or update people (APP-113 §4a): manual records, for when the sync is off. */
  'people',
  'programs',
  'opening_budgets',
  'past_awards',
]);
export type ImportKind = z.infer<typeof ImportKind>;

/** Imports a System Admin runs from the Admin area; opening budgets live under Budget pools. */
export const AdminImportKind = z.enum(['directory_fields', 'people', 'programs', 'past_awards']);
export type AdminImportKind = z.infer<typeof AdminImportKind>;

export const ImportStatus = z.enum(['previewed', 'applied', 'discarded']);

/** One change the import makes, with the value now and the value after (FR5, SYS3). */
export const ImportChange = z.object({
  /** The file line the row is on (the header is line 1). */
  row: z.number().int().positive(),
  target: z.object({
    type: z.enum(['person', 'program', 'budget', 'award']),
    /** Null for something the import creates. */
    id: z.string().nullable(),
    label: z.string(),
  }),
  /** How the row found what it changes. */
  matchedOn: z
    .enum([
      'person_id',
      'ee_number',
      'email',
      'sign_in_name',
      'entra_object_id',
      'system_id',
      'name',
      'pool',
      'award_id',
    ])
    .nullable(),
  /** The field, as the screen names it: "Sector", "Home org", "Initial budget", … */
  field: z.string(),
  /** Shown values (text), never internal IDs. Null when the field is blank now. */
  before: z.string().nullable(),
  after: z.string().nullable(),
  /** `fill`: a blank becomes a value. `overwrite`: a value changes. `create`: something new. */
  effect: z.enum(['fill', 'overwrite', 'create']),
});
export type ImportChange = z.infer<typeof ImportChange>;

export const ImportRejectedRow = z.object({
  row: z.number().int().positive(),
  /** What the row named ("EE# 10588", "Maritime Air · Radar"), to find it in the file. */
  key: z.string().nullable(),
  reasons: z.array(z.string()).min(1),
});
export type ImportRejectedRow = z.infer<typeof ImportRejectedRow>;

/** What an opening budget or past award import does to a pool's year (X4, FR3). */
export const ImportPoolImpact = z.object({
  pool: Ref,
  fiscalYear: z.number().int(),
  /** The pool's budget for the year after the import (null: still not set). */
  budget: Amounts.nullable(),
  /** Held and paid before the import. */
  usedBefore: Amounts,
  /** What this import adds: budget for opening budgets, awarded for past awards. */
  imported: Amounts,
  remainingAfter: Amounts.nullable(),
  overBudget: z.boolean(),
});
export type ImportPoolImpact = z.infer<typeof ImportPoolImpact>;

export const ImportPreview = z.object({
  id: z.uuid(),
  kind: ImportKind,
  status: ImportStatus,
  /** Send it back with apply: a preview worked out again since gets a new version. */
  version: z.number().int().positive(),
  fileName: z.string(),
  uploadedBy: Ref,
  uploadedAt: DateTime,
  previewedAt: DateTime,
  appliedBy: Ref.nullable(),
  appliedAt: DateTime.nullable(),
  /** The audit batch holding every change the import made (once applied). */
  batchId: z.uuid().nullable(),
  counts: z.object({
    rows: Count,
    /** Rows that will change something or found their match with nothing to change. */
    matched: Count,
    /** Rows that matched and would change nothing. */
    unchanged: Count,
    rejected: Count,
    changes: Count,
    fills: Count,
    overwrites: Count,
    creates: Count,
  }),
  changes: z.array(ImportChange),
  rejected: z.array(ImportRejectedRow),
  /** Opening budgets and past awards: each pool and year the import touches. */
  pools: z.array(ImportPoolImpact),
  /** The columns the import read, in file order. */
  columns: z.array(z.string()),
  /** Previewed, with at least one change, and the caller may apply it. */
  canApply: z.boolean(),
});
export type ImportPreview = z.infer<typeof ImportPreview>;

export const ApplyImport = z.object({ version: z.number().int().positive() }).strict();
export type ApplyImport = z.infer<typeof ApplyImport>;

export const ImportSummary = ImportPreview.pick({
  id: true,
  kind: true,
  status: true,
  version: true,
  fileName: true,
  uploadedBy: true,
  uploadedAt: true,
  appliedBy: true,
  appliedAt: true,
  batchId: true,
  counts: true,
});
export type ImportSummary = z.infer<typeof ImportSummary>;

export const ImportList = z.object({ items: z.array(ImportSummary) });
export type ImportList = z.infer<typeof ImportList>;

export const ImportListQuery = z
  .object({
    kind: ImportKind.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict();
export type ImportListQuery = z.infer<typeof ImportListQuery>;

// ---------------------------------------------------------------------------------------------
// Year rollover: next year's draft budgets, the settings review, archiving a year
// ---------------------------------------------------------------------------------------------

export const BudgetDraftRow = z.object({
  pool: Ref,
  cashCents: z.number().int().nonnegative(),
  options: z.number().int().nonnegative(),
  /** The year it was copied from, or null when it was typed. */
  copiedFromYear: z.number().int().nullable(),
  /** What the pool had for the year copied from, to compare. */
  lastYear: Amounts.nullable(),
  updatedBy: Ref,
  updatedAt: DateTime,
});
export type BudgetDraftRow = z.infer<typeof BudgetDraftRow>;

export const BudgetDraft = z.object({
  fiscalYear: z.number().int(),
  status: z.enum(['none', 'draft', 'confirmed']),
  /** Send it back to edit or confirm: any edit to the draft gives a new version. */
  version: z.number().int().nonnegative(),
  rows: z.array(BudgetDraftRow),
  /** Pools with a budget for the year already: a draft never replaces one. */
  alreadySet: z.array(Ref),
  confirmedBy: Ref.nullable(),
  confirmedAt: DateTime.nullable(),
  canEdit: z.boolean(),
});
export type BudgetDraft = z.infer<typeof BudgetDraft>;

export const CopyBudgets = z
  .object({ fiscalYear: Year, fromYear: Year, reason: z.string().trim().max(2000).optional() })
  .strict();
export type CopyBudgets = z.infer<typeof CopyBudgets>;

export const EditBudgetDraft = z
  .object({
    fiscalYear: Year,
    version: z.number().int().nonnegative(),
    rows: z
      .array(
        z
          .object({
            poolId: z.string().min(1).max(100),
            cashCents: z.number().int().nonnegative().max(1e13),
            options: z.number().int().nonnegative().max(1e9),
          })
          .strict(),
      )
      .min(1)
      .max(50),
  })
  .strict();
export type EditBudgetDraft = z.infer<typeof EditBudgetDraft>;

export const ConfirmBudgetDraft = z
  .object({ fiscalYear: Year, version: z.number().int().nonnegative(), reason: Reason })
  .strict();
export type ConfirmBudgetDraft = z.infer<typeof ConfirmBudgetDraft>;

export const DiscardBudgetDraft = z
  .object({ fiscalYear: Year, version: z.number().int().nonnegative(), reason: Reason })
  .strict();
export type DiscardBudgetDraft = z.infer<typeof DiscardBudgetDraft>;

export const BudgetDraftQuery = z.object({ fiscalYear: Year }).strict();

/** A setting as it will apply in the new year: settings are dated, so they carry on by themselves. */
export const CarriedSetting = z.object({
  key: z.string(),
  label: z.string(),
  /** A short plain summary of the value in force on the first day of the year. */
  value: z.string(),
  effectiveFrom: PlainDate.nullable(),
  lastChangedAt: DateTime.nullable(),
  /** Something to look at before the year starts (an FMV more than a year old, say). */
  attention: z.string().nullable(),
});
export type CarriedSetting = z.infer<typeof CarriedSetting>;

export const YearStatus = z.object({
  year: z.number().int(),
  archived: z.boolean(),
  archivedBy: Ref.nullable(),
  archivedAt: DateTime.nullable(),
  archiveReason: z.string().nullable(),
});
export type YearStatus = z.infer<typeof YearStatus>;

/** SYS1 "Data & year rollover": the three steps to start a new year, worked out from the data. */
export const Rollover = z.object({
  fromYear: z.number().int(),
  toYear: z.number().int(),
  budgets: z.object({
    status: z.enum(['to_do', 'draft', 'confirmed']),
    pools: Count,
    by: Ref.nullable(),
    at: DateTime.nullable(),
  }),
  settings: z.object({
    status: z.enum(['to_do', 'reviewed']),
    reviewedBy: Ref.nullable(),
    reviewedAt: DateTime.nullable(),
    items: z.array(CarriedSetting),
  }),
  archive: z.object({
    status: z.enum(['later', 'ready', 'blocked', 'archived']),
    /** Awards charged to the year that aren't closed or ended yet. */
    inProgress: Count,
    /** The earliest day it can be archived: the day after the year ends. */
    from: PlainDate,
    year: YearStatus,
  }),
});
export type Rollover = z.infer<typeof Rollover>;

export const RolloverQuery = z.object({ toYear: Year.optional() }).strict();

export const ReviewSettings = z
  .object({ year: Year, note: z.string().trim().max(2000).optional() })
  .strict();
export type ReviewSettings = z.infer<typeof ReviewSettings>;

export const YearChange = z.object({ reason: Reason }).strict();
export type YearChange = z.infer<typeof YearChange>;

// ---------------------------------------------------------------------------------------------
// Support tools: reopen a closed award, release a stuck Shareworks claim
// ---------------------------------------------------------------------------------------------

export const ReopenLine = z.object({
  id: z.uuid(),
  person: Ref,
  stage: z.enum(['closed', 'paid', 'other']),
  closedAt: DateTime.nullable(),
  /** When it was entered in Shareworks, and by whom: reopening keeps both. */
  shareworksEnteredAt: DateTime.nullable(),
  shareworksEnteredBy: Ref.nullable(),
  /** The earning statement on file, which stays on the line. */
  statement: z.object({ documentId: z.uuid(), fileName: z.string() }).nullable(),
});
export type ReopenLine = z.infer<typeof ReopenLine>;

export const ReopenCandidate = z.object({
  award: z.object({
    id: z.uuid(),
    displayId: z.string(),
    title: z.string(),
    type: z.string(),
    fiscalYear: z.number().int(),
    closedAt: DateTime.nullable(),
    imported: z.boolean(),
    version: z.number().int(),
  }),
  lines: z.array(ReopenLine),
  /** Null when it can be reopened; otherwise the plain reason why not. */
  blocked: z.string().nullable(),
});
export type ReopenCandidate = z.infer<typeof ReopenCandidate>;

export const ReopenAward = z
  .object({
    version: z.number().int().positive(),
    reason: Reason,
    /** The people to reopen; all of them when left out. */
    lineIds: z.array(z.uuid()).min(1).max(200).optional(),
  })
  .strict();
export type ReopenAward = z.infer<typeof ReopenAward>;

export const ReopenResult = z.object({
  award: ReopenCandidate.shape.award,
  reopened: z.array(z.uuid()),
});
export type ReopenResult = z.infer<typeof ReopenResult>;

export const StuckClaim = z.object({
  id: z.uuid(),
  kind: z.enum(['award', 'shareworks']),
  holder: Ref,
  /** The award, and for Shareworks the person's row. */
  award: z.object({ id: z.uuid(), label: z.string() }),
  person: Ref.nullable(),
  claimedAt: DateTime,
  lastActivityAt: DateTime,
  /** Idle past the claim timeout: anyone may take it, and it's listed here to release. */
  idle: z.boolean(),
});
export type StuckClaim = z.infer<typeof StuckClaim>;

export const StuckClaims = z.object({
  timeoutMinutes: Count,
  items: z.array(StuckClaim),
});
export type StuckClaims = z.infer<typeof StuckClaims>;

export const ReleaseShareworksClaims = z
  .object({ claimIds: z.array(z.uuid()).min(1).max(200), reason: Reason })
  .strict();
export type ReleaseShareworksClaims = z.infer<typeof ReleaseShareworksClaims>;

export const ReleaseShareworksResult = z.object({
  released: z.array(z.uuid()),
  /** Claims already released, or not Shareworks claims: nothing done for these. */
  skipped: z.array(z.uuid()),
});
export type ReleaseShareworksResult = z.infer<typeof ReleaseShareworksResult>;
